import i18next from 'i18next'
import axios, { AxiosInstance } from 'axios'
import Cookies from 'js-cookie'
import { orgs, snackBar } from 'store'
import userStore from 'store/user.store'
import type { UserMessage, User, CsvUser } from 'utils/models'
import {
  ID,
  UserGroup,
  MessageOp,
  CreateCommunityReq,
  UserGroupCreateReq,
  MessageResponseOp,
  GroupType,
  ModuleEnum,
  ProviderEnum,
  CommunityUpdateReq,
  AlarmMessage
} from 'utils/types'
import { intToBool } from 'utils/converts'
import { readApiErrorFields, readApiErrorMessages } from './apiError'
import { newGuid } from 'utils/generates'
import { ModuleType } from 'utils/types'
import { RelateUserGroupOp, OperationType } from 'utils/operation'
import { truncate } from 'utils/strings'

export const endpoint = {
  newAlarmMessage: () => `/sapi/deviceLocation/alert`,
  updateLocation: (messageId: number) => `/sapi/deviceLocation/alert/${messageId}`,
  getDeviceLocation: () => `/sapi/deviceLocation`,
  getGroupUsers: (groupId: number) => `/sapi/userinfo/group/${groupId}`,
  getGroupMessages: (groupId = 0, pageNumber = 1, pageSize = 20 ) => `/sapi/messages/${groupId}/?pageNumber=${pageNumber}&pagesize=${pageSize}`,
  getGroups: (communityId: number, otherUserId: string | number = '') => `/sapi/groups/community/${communityId}/${otherUserId}`,
  getLastUserLocation: (communityId: number) => `/sapi/deviceLocation/${communityId}`,
  getLocationLog: (communityId: number) => `/sapi/deviceLocation/log/${communityId}`,
  
  getBrands: () => `/sapi/brands`,

  getCommunitySettings: (communityId: number) => `/sapi/communitysettings/${communityId}`,
  getCommunities: () => `/sapi/communities`,
  updateCommunities: (communityId: number) => `/sapi/communities/${communityId}`,
  manageCommunities: () => `/sapi/communities/manage`,
  manageCommunity: (communityId: number) => `/sapi/communities/manage/${communityId}`,
  updateCommunity: (communityId: number) => `sapi/communities/features/${communityId}`,
  getCommunitiesUsers: (communityId = 0) => `/sapi/userinfo/community/${communityId}`,
  getCommunityMessages: (communityId = 0) => `/sapi/messages/community/${communityId}/`,
  getCommunityModules: (communityId: string | number = '') => `/sapi/modules/${communityId}/true`,
  getCommunityDirectorModules: (communityId: string | number = '') => `sapi/managemodules/director/${communityId}`,
  manageCommunityModules: (moduleId: number | string = '') => `/sapi/managemodules/${moduleId}`,
  manageCommunityModulesByDirector: (moduleId: number | string = '', enabled: boolean) => `sapi/managemodules/status/${moduleId}/${enabled}`, 
  getOpenSupportCalls: (communityId = 0) => `/sapi/groups/supportcalls/${communityId}`,
  getSupportGroups: (communityId: number, otherUserId: string | number = '') => `/sapi/groups/support/${communityId}/${otherUserId}`,
  setUserCommunity: (communityId: number, otherUserId: number | null = null, enable?: boolean, showInAlphon?: boolean) => (
    `/sapi/userinfo/community/${communityId}/?otherUserId=${otherUserId}${enable !== undefined ? `&enable=${enable}` : ''}${showInAlphon !== undefined ? `&showInAlphon=${showInAlphon}` : ''}`
  ),
  login: () => '/api/users/login',
  logout: () => '/api/users/logout/3',
  verify: () => '/api/users/verify',
  refreshToken: () => '/api/users/auth/V-13.3.6-WMC/3',

  getMessageResponses: (messageId: number, date = '') => `/sapi/messages/response/${messageId}/${date}`,
  getGroupSendingProviders: () => `/sapi/GroupSendingProvider`,
  updateGroupSendingProviders: (id: any) => `/sapi/GroupSendingProvider/${id}`,
  updateOrgSendingProviders: (id: any) => `/sapi/CommunitySendingProviders/${id}`,
  getCommunitySendingProviders: () => `/sapi/CommunitySendingProviders`,

  openModule: (moduleId: number) => `/sapi/modules/open/${moduleId}`,
  clickModule: (moduleId: number, communityId: number) => `/sapi/modules/click/${moduleId}/${communityId}`,
  shareModule: (moduleId: number, communityId: number) => `/sapi/modules/share/${moduleId}/${communityId}`,
  resetModule: (moduleId: number, communityId: number) => `/sapi/modules/resetmoduledata/${moduleId}/${communityId}`,

  paymentLogin: (communityId: number) => `/sapi/payments/otp1/${communityId}`,
  paymentVerify: (communityId: number, pinCode: number) => `/sapi/payments/otp2/${communityId}/${pinCode}`,
  getPayment: (communityId: number) => `/sapi/payments/${communityId}/`,
  downloadPayment: (communityId: number, fileUrl = false) => `/sapi/payments/download/${communityId}/${fileUrl}`,
  resendPayment: (communityId: number) => `/sapi/payments/resend/${communityId}/`,
  forgetPayment: (communityId: number) => `/sapi/payments/forget/${communityId}/`,

  getProviders: () => `/sapi/providers`,
  getCommunityProvider: (providerId: number) => `/sapi/providers/community/${providerId}`,
  updateCommunityProvider: (providerId: number, communityId: number) => `/sapi/providers/${providerId}/community/${communityId}`,

  deleteProvider: (providerId: number, communityId: number) => `/sapi/providers/${providerId}/community/${communityId}`,
  setGroupProvider: (groupId: number, providerId: number) => `/sapi/groups/${groupId}/provider/${providerId}`,
  deleteGroupProvider: (groupId: number) => `/sapi/groups/${groupId}/provider`,
  deleteUserPushTokens: (userId: number) => `/sapi/pushtoken/user/${userId}`,

  getAssociatedGroups: () => '/sapi/entities/',
  createAssociatedGroups: () => `/sapi/Entities`,
  updateAssociatedGroups: (entityId: number) => `/sapi/Entities/${entityId}`,
  getAssociatedLabels: (entityId: number) => `/sapi/entities/labels/${entityId}`,
  getEntityLabelLinks: (entityId: any) => `/sapi/EntityLabels/entity/${entityId}`,
  getAllLabels: () => `/sapi/labels/entity/`,
  getAssociatedLabelsV2: (entityId: number) => `/sapi/labels/entity/${entityId}`,
  getAssociatedUsers: (entityId: number) => `/sapi/Entities/user/${entityId}`,
  createEntityUser: () => `/sapi/entities/user`,
  editEntityUser: (id: any) => `/sapi/Entities/user/${id}`, 
  deleteEntityUser: (entityId: number, userId: number) => `/sapi/Entities/${entityId}/user/${userId}`,
  createLabelAssociatedEntity: (entityId: any) => `/sapi/Groups/7016/labels/${entityId}`,
  createLabel: () => `/sapi/Labels`,
  linkLabelWithEntity: () => `/sapi/EntityLabels`,
  unlinkLabelWithEntity: (id: any) => `/sapi/EntityLabels/${id}`,
  editEntityLabel: (labelId: any) => `/sapi/labels/${labelId}`,

  createCommunity: () => '/sapi/communities/',

  createGroup: () => '/sapi/groups/',
  updateGroup: (groupId: number) => `/sapi/groups/${groupId}`,
  deleteGroupCommunity: (groupId: number, communityId: number) => `/sapi/groups/${groupId}/community/${communityId}`,
  updateGroupUsers: (groupId: number) => `/sapi/userinfo/bulk/group/${groupId}`,
  updateUserGroups: (userId: number) => `/sapi/userinfo/bulk/group/user/${userId}`,
  updateGroupLabels: (groupId: number, mcEntityId: number) => `/sapi/groups/${groupId}/labels/${mcEntityId}`,

  createUser: () => '/sapi/userinfo/',
  updateUser: (userId: number) => `/sapi/userinfo/${userId}`,
  createUsers: () => '/sapi/userinfo/bulk',
  
  getUserManagementList: (communityId: any) => `/sapi/UserEntity/basicinfo/${communityId}?DisablePaging=true`,
  getCategories: (communityId: number) => `/api/categories?communityId=${communityId}`,
  createUserManagementOne: (communityId: any) => `/sapi/UserEntity/${communityId}`,
  getUserManagementOne: (id: number, communityId: any) => `/sapi/UserEntity/${communityId}/${id}`,
  relateUserToCommunity: (communityId: number, userId: number) => `/sapi/UserEntity/${communityId}/${userId}/relate`,
  // The server declares this one with a leading slash - [HttpPut("/default-community/...")] -
  // so it answers at the host root rather than under /sapi like the rest of UserEntity.
  changeUserDefaultCommunity: (userId: number, communityId: number) => `/default-community/${communityId}/${userId}`,
  getUserManagementOneFamily:  (id: number, communityId: any) => `/sapi/HouseHold/${id}/community/${communityId}`,
  getUserManagementTags: (communityId: any) => `/sapi/Tags/available/${communityId}`,
  getUserManagementTagTypes: () => `/sapi/Tags/types`,

  deleteUserCommunity: (communityId: number, userId: number) => `/sapi/userinfo/community/${communityId}/user/${userId}`,
  updateUsersCommunityRelate: (communityId: number) => `/sapi/userinfo/bulk/community/${communityId}`,
  updateUserCommunityRelate: (communityId: number, userId: number) => `/sapi/userinfo/community/${communityId}/user/${userId}`,

  sendMessage: (groupId: number, schedule: boolean, isSupport?: boolean) => `/sapi/messages/${isSupport ? 'support/' : ''}${groupId}/${schedule}`,
  updateMessage: (messageId: number) => `/sapi/messages/${messageId}`,
  updateMessageWithMedia: (messageId: number) => `/sapi/messages/media/${messageId}`,
  responseMessage: (messageId: number) => `/sapi/messages/response/message/${messageId}`,
  messageDetails: (messageId: number) => `/sapi/messages/response/${messageId}`,
  responseMessages: () => '/sapi/messages/response/bulk',
  responseGroupMessages: (groupId: number) => `/sapi/messages/response/group/${groupId}`,
  deleteMessage: (id: number) => `/sapi/messages/${id}`,
  sendScheduled: (messageId: number) => `/sapi/Messages/sendScheduled/${messageId}`,

  signEula: (userId: number) => `/sapi/legal/sign/${userId}`,
  uploadIsraelId: (brandId: number) => `/sapi/userinfo/bulk/id/${brandId}`
}

const mapGroupsDescription = (groups: Array<any>, groupType: GroupType, isCommunityDirector = false) => {
  if (groups?.length > 0) {
    const groupsMap = groups.map<any>((g: any) => {
      return ({
        group: {
          community_id: g.community_id,
          id: isCommunityDirector ? -1 : g.id,
          description: /^{"/.test(g.description) ? JSON.parse(g.description) : g.description,
          name: g.name,
          icon: g.icon,
          members: g.members,
          orgainzers: g.orgainzers,
          group_is_member: g.group_is_member != null ? g.group_is_member : false,
          group_is_subscriber: g.group_is_subscriber != null ? g.group_is_subscriber : false,
          group_is_favourite: g.group_is_favourite != null ? g.group_is_favourite : false,
          group_is_sender: g.group_is_sender != null ? g.group_is_sender : false,
          duplex: g.duplex,
          is_organizer: g.is_organizer,
          parent_id: g.parent_id,
          providerId: g.providerId || null,
          providerName: g.providerName || null,
          sendSMS: g.sendSMS || false,
          sendEmail: g.sendEmail || false,
          allowShare: g.allowShare || false,
          type: groupType
        },
        community: g.community_id,
        is_favourite: g.is_favourite,
        is_organizer: g.is_organizer,
        is_sender: g.is_sender,
        is_subscriber: g.is_subscriber,
        is_member: g.is_member,
        user: g.user_id,
        parent_id: g.parent_id
      })
    })
    return groupsMap
  }
  return []
}

const PROTOCOL_VERSION = 'V-12.0'
class Api {

  constructor(secure: AxiosInstance) {
    this.secure = secure
    this.jwtToken = () => userStore.jwtToken
  }

  jwtToken: () => string

  secure: AxiosInstance

  newFetcher = async (URL: string, method: 'PATCH' | 'POST' | 'GET' | 'PUT' | 'DELETE' = 'POST', data: any, additionalHeaders: { [a: string]: string } = {}, iteration: number = 0): Promise<any> => {
    if (iteration >= 2) {
      return
    }
    const isFormData = data instanceof FormData
    const headers: any = {}
    if (this.jwtToken()) {
      headers['Authorization'] = `Bearer ${this.jwtToken() || ''}`;
    }
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    Object.entries(additionalHeaders).forEach(el => {
      headers[el[0]] = el[1];
    })
    let body: string | undefined | FormData = data ? (data instanceof FormData ? data : JSON.stringify(data)) : undefined;
    body = (body === '{}') ? undefined : body
    try {
      if (method === 'GET' || method === 'DELETE') {
        const response = await this.secure[method.toLowerCase() as 'get'](URL, { ...(body ? { params: data } : {}), headers, withCredentials: true });
        return response.data;
      } else {
        const response = await this.secure[method.toLowerCase() as 'post'](URL, body, { headers, withCredentials: true });
        return response.data;
      }
    } catch (e) {
      const err = e as any;
      const { errors } = err?.response?.data || {}
      const statusError = 'Error ' + err?.request?.status || 500;
      // What the server actually said - 'The Description field is required.' -
      // rather than axios' 'Request failed with status code 400'. Falls back to
      // the generic server message, so the snackbar is never blank.
      const serverMessages = readApiErrorMessages(err?.response?.data)
      const fail = {
        success: false,
        statusCode: err?.request?.status || 500,
        status: err?.request?.status || 500,
        data: err?.response?.data,
        errorDescription: serverMessages.join(' ') || i18next.t('app:server_problem'),
        errorFields: readApiErrorFields(err?.response?.data),
        errors: Array.isArray(errors) ? errors
          : serverMessages.length ? serverMessages
          : [err?.message || statusError],
        error: err?.message || statusError,
      }

      if (fail.status === 401) {
        if (iteration === 1) {
          return fail
        }
        if (URL != endpoint.refreshToken() && URL != endpoint.logout()) {
          const refreshRes = await this.refreshToken()
          if (refreshRes.success || refreshRes.statusCode === 200) {
            return await this.newFetcher(URL, method, data, additionalHeaders, ++iteration)
          }
        }
        return fail
      }
      return fail

    }
    
  }


  newProtocolFetcher = async (URL: string, method: 'PATCH' | 'POST' | 'GET' | 'PUT' | 'DELETE' = 'POST', data: any, additionalHeaders: { [a: string]: string } = {}, iteration: number = 0): Promise<any> => {
    if (iteration >= 2) {
      return
    }
    const isFormData = data instanceof FormData
    const headers: any = {}
    /*if (this.jwtToken()) {
      headers['Authorization'] = `Bearer ${this.jwtToken() || ''}`;
    }*/
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    Object.entries(additionalHeaders).forEach(el => {
      headers[el[0]] = el[1];
    })
    let body: string | undefined | FormData = data ? (data instanceof FormData ? data : JSON.stringify(data)) : undefined;
    body = (body === '{}') ? undefined : body
    try {
      if (method === 'GET' || method === 'DELETE') {
        const response = await this.secure[method.toLowerCase() as 'get'](URL, { ...(body ? { params: data } : {}), headers, withCredentials: true });
        return response.data;
      } else {
        const response = await this.secure[method.toLowerCase() as 'post'](URL, body, { headers, withCredentials: true });
        return response.data  || { result: true };
      }
    } catch (e) {
      const err = e as any;
      const { errors } = err?.response?.data || {}
      const statusError = 'Error ' + err?.request?.status || 500;
      // What the server actually said - 'The Description field is required.' -
      // rather than axios' 'Request failed with status code 400'. Falls back to
      // the generic server message, so the snackbar is never blank.
      const serverMessages = readApiErrorMessages(err?.response?.data)
      const fail = {
        success: false,
        statusCode: err?.request?.status || 500,
        status: err?.request?.status || 500,
        data: err?.response?.data,
        errorDescription: serverMessages.join(' ') || i18next.t('app:server_problem'),
        errorFields: readApiErrorFields(err?.response?.data),
        errors: Array.isArray(errors) ? errors
          : serverMessages.length ? serverMessages
          : [err?.message || statusError],
        error: err?.message || statusError,
      }

      if (fail.status === 401) {
        if (iteration === 1) {
          return fail
        }
        if (URL != endpoint.refreshToken() && URL != endpoint.logout()) {
          const refreshRes = await this.refreshToken()
          if (refreshRes.success || refreshRes.statusCode === 200) {
            return await this.newProtocolFetcher(URL, method, data, additionalHeaders, ++iteration)
          }
        }
        return fail
      }
      return fail

    }
  }

  // A getter, not a constant: this is read long after the module loads, and
  // has to follow the language in force at that moment.
  get genericErrorText() { return i18next.t('app:server_problem') }
  /**
   *
   * @param res api response
   * @param handleError whether there should be a snackbar displayed if theres an error
   */

  generateResponse = (res: any, handleError: boolean = true): { success: boolean; data: any; error: string } => {
    try {
      let data = typeof res === 'string' || res instanceof String ? JSON.parse(res?.toString()) : res
      if ((res.statusCode >= 400) || !data) {
          if (handleError) {
          snackBar.showError(data?.errorDescription)
        }
        return { success: false, data: undefined, error: data?.errors?.[0]?.errorDescription }
      } else {
        return { success: true, data: data?.data, error: data?.errorDescription }
      }
    }
    catch (e) {
      const err = e as string;
      console.log('genericResponse error ', err)
      return { success: false, data: undefined, error: err }
    }
  }

  getCode = async (phone: string) => {
    const data = {
      protocol: PROTOCOL_VERSION,
      Username: phone,
      RegistrationType: 3
    }
    const res = await this.newFetcher(endpoint.login(), 'POST', data)
    return this.generateResponse(res, false)

  }

  refreshToken = async (): Promise<any | undefined> => {
    const res = await this.newFetcher(endpoint.refreshToken(), "GET", '')
    if (res?.statusCode === 401 || res?.statusCode === 400) {
      userStore.cleenData()
      return res
    }
    const jwt = res?.data
      if (jwt && jwt.token) {
      userStore.login({ ...userStore.user, user_token: jwt.token})
    }
    return res
  }

  checkCode = async (code: string, tempToken: string): Promise<any | undefined> => {
    const data = {
      pinCode: code
    }
    try {
      const headers = { ['Authorization']: (tempToken && `Bearer ${tempToken}`) || '' }
      const res = await this.newFetcher(endpoint.verify(), 'POST', data, headers)
      // const res = await this.goApi.post('/api/users/verify', data, { headers, withCredentials: trues })
      const genericResponse = this.generateResponse(res, false);
      if (genericResponse?.error) {
        snackBar.showError(genericResponse?.error)
      }
      return genericResponse
    } catch (e) {
      return ({ success: false, statusCode: 401, errorDescription: 'error', error: 'error' })
    }
  }


  logout = async (): Promise<any> => {
    const result = await this.newFetcher(endpoint.logout(), 'GET', null)
    if (result.errors?.length) {
      snackBar.showError(result.errors[0]?.errorDescription)
    }
    return result;
  }

  // Every community in the system, not just the ones the signed-in manager
  // belongs to. Admin-only on the server, which is also who the only caller is.
  getManageCommunities = async () => {
    const res = await this.newFetcher(endpoint.manageCommunities(), 'GET', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }
    return res || []
  }

  getCommunities = async () => {
    const res = await this.newFetcher(endpoint.getCommunities(), 'GET', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }
    return res || []
  }

  assignUserManagementTag = async(data: any) => { 
    const res = await this.newFetcher(`/sapi/UserTags/assign`, 'POST', data)
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res || null
  }

  unassignUserManagementTag = async(data: any) => { 
    const res = await this.newFetcher(`/sapi/UserTags/remove`, 'POST', data)
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res || null
  }

  assingUserManagementHousehold = async (data: any) => {
    const res = await this.newFetcher(`/sapi/HouseHold/assign`, 'POST', data)
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res || null
  }

  unassingUserManagementHousehold = async (userId: any, communityId: any) => {
    const res = await this.newFetcher(`/sapi/HouseHold/remove/${userId}/community/${communityId}`, 'GET', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return {}
    }

    return res
  }

  getHouseholds = async () => {
    const res = await this.newFetcher(`/sapi/HouseHold/community/1`, 'GET', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }
    return res || []
  }

  createUserManagementHousehold = async (data: any) => {
    const res = await this.newFetcher(`/sapi/HouseHold/create-and-assign`, 'POST', data)
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }

    return res || null
  }

  createUserManagementOne = async (data: any, communityId: any) => {
    const res = await this.newFetcher(endpoint.createUserManagementOne(communityId), 'POST', data)
    if (res?.status && res.status >= 400) {
      if (res.status === 409 && res.data?.code === 'DUPLICATE_USER') {
        const u = res.data.existingUser
        const name = [u.existingGivenName, u.existingFamilyName].filter(Boolean).join(' ')
        const identifier = u.existingIsraeliId
          ? i18next.t('app:dup_by_id', { id: u.existingIsraeliId })
          : u.existingMainMobile
          ? i18next.t('app:dup_by_phone', { phone: u.existingMainMobile })
          : u.existingEmail
          ? i18next.t('app:dup_by_email', { email: u.existingEmail })
          : i18next.t('app:dup_identifier')
        snackBar.showError(`${i18next.t('app:dup_user_exists', { identifier })}${name ? ` (${name})` : ''}`)
        return res
      }
      
      if (res.status === 422) {
        throw { status: 422, data: res.data }
      }
      snackBar.showError(res.errorDescription)
      return null
    }

    return res || null
  }

  updateUserManagementOne = async (id: number, data: any, communityId: any) => {
    const res = await this.newFetcher(endpoint.getUserManagementOne(id, communityId), 'PUT', data)
    if (res?.status && res.status >= 400) {
      if (res.status === 409 && res.data?.code === 'DUPLICATE_USER') {
        const u = res.data.existingUser
        const name = [u.existingGivenName, u.existingFamilyName].filter(Boolean).join(' ')
        const identifier = u.existingIsraeliId
          ? i18next.t('app:dup_by_id', { id: u.existingIsraeliId })
          : u.existingMainMobile
          ? i18next.t('app:dup_by_phone', { phone: u.existingMainMobile })
          : u.existingEmail
          ? i18next.t('app:dup_by_email', { email: u.existingEmail })
          : i18next.t('app:dup_identifier')
        snackBar.showError(`${i18next.t('app:dup_user_exists', { identifier })}${name ? ` (${name})` : ''}`)
        return res
      } else {
        snackBar.showError(res.errorDescription)
      }
      return null
    }

    return res || null
  }

  assingUserManagementFamily = async (payload: any) => {
    const res = await this.newFetcher('/sapi/UserRelation/add', 'POST', payload)
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    
    return res || null
  }

  getOneHousehold = async (userId: any, communityId: any) => {
    const res = await this.newFetcher(`/sapi/UserEntity/UserProfile/${communityId}/${userId}`, 'GET', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }

    return res || null
  }

  getUserManagementFamilyRelations = async (userId: any, communityId: any) => {
    const res = await this.newFetcher(`/sapi/UserRelation/${userId}/community/${communityId}/family-summary`, 'GET', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }

    return res || []
  }

  relateUserToCommunity = async (communityId: number, userId: number) => {
    const res = await this.newFetcher(endpoint.relateUserToCommunity(communityId, userId), 'POST', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res || null
  }

  removeUserManagementUser = async (userId: any, communityId: any) => {
    const res = await this.newFetcher(`/sapi/UserEntity/${communityId}/users/${userId}`, 'DELETE', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    
    return res || null
  }

  assingUserManagementTags = async (payload: any) => {
    const res = await this.newFetcher('/sapi/UserTags/assign', 'POST', payload)
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    
    return res || null
  }

  unassingUserManagementTags = async (payload: any) => {
    const res = await this.newFetcher('/sapi/UserTags/remove', 'POST', payload)
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    
    return res || null
  }

  getAssingUserManagementTags = async (userId: any, communityId: any) => {
    const res = await this.newFetcher(`/sapi/UserTags/${userId}/community/${communityId}`, 'GET', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return {
        items: []
      }
    }

    return res || { items: [] }
  }

  getUserManagementTags = async (communityId: any) => {
    const res = await this.newFetcher(endpoint.getUserManagementTags(communityId), 'GET', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return {
        items: []
      }
    }

    return res || { items: [] }
  }

  getUserManagementTagTypes = async () => {
    const res = await this.newFetcher(endpoint.getUserManagementTagTypes(), 'GET', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return {
        items: []
      }
    }
    
    return res || { items: [] }
  }

  // Handed back as it came, failures included: the stored procedure answers with
  // its own error codes and the caller turns them into messages, so swallowing
  // the response here would leave it with nothing to report.
  changeUserDefaultCommunity = async (userId: number, communityId: number) => {
    return await this.newFetcher(endpoint.changeUserDefaultCommunity(userId, communityId), 'PUT', {})
  }

  getUserManagementOne = async (id: number, communityId: any) => {
    const res = await this.newFetcher(endpoint.getUserManagementOne(id, communityId), 'GET', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return {
        data: null
      }
    }
    return res || { data: null }
  }

  removeUserManagementFamilyRelation = async (data: any) => {
    const res = await this.newFetcher(`/sapi/UserRelation/remove`, 'POST', data)
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }

    return res || null
  }

  getUserManagementOneFamily = async (id: number, communityId: any) => {
    const res = await this.newFetcher(endpoint.getUserManagementOneFamily(id, communityId), 'GET', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return {
        data: null
      }
    }
    return res || { data: null }
  }

  fetchProtocolCountsByStatus = async (baseUrl: any, authToken: any) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/protocols/counts-by-status`, 'GET', {}, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res
  }

  getProtocolStatusHistory = async (baseUrl: any, authToken: any, protocolId: number, query: any) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/protocols/${protocolId}/status-history`, 'GET', query, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res
  }


  fetchProtocols = async (baseUrl: any, authToken: any, body: object) => {
    const headers: Record<string, string> = {'Authorization': `Bearer ${authToken}`}
    const res = await this.newProtocolFetcher(`${baseUrl}api/protocols/get-list`, 'POST', body, headers)
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res
  }

  getArchiveList = async (baseUrl: any, authToken: any, body: any) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/protocols/archive/get-list`, 'POST', body, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res
  }

  getCategories = async (baseUrl: any, authToken: any, path: any) => {
    const res = await this.newProtocolFetcher(`${baseUrl}${path}`, 'GET', {}, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }
    return res || []
  }

  // Trades the mekome token for a one-time code; the service desk answers with
  // the url that logs the user in with it.
  requestServiceDeskSsoCode = async (baseUrl: string, authToken: string, communityId: number) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/v1/mekome/sso/code`, 'POST', { communityId }, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) return null
    return res
  }

  createCategory = async (baseUrl: any, authToken: any, body: any) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/categories`, 'POST', body, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      // The reason travels back with the failure: the category form shows it
      // inline too, so it is still on screen once the snackbar auto-hides, and
      // marks the fields the server named.
      return { error: res.errorDescription, errorFields: res.errorFields || [] }
    }
    return res
  }

  updateCategory = async (baseUrl: any, authToken: any, categoryId: number, body: any) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/categories/${categoryId}`, 'PUT', body, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      // The reason travels back with the failure: the category form shows it
      // inline too, so it is still on screen once the snackbar auto-hides, and
      // marks the fields the server named.
      return { error: res.errorDescription, errorFields: res.errorFields || [] }
    }
    return res
  }

  deleteCategory = async (baseUrl: any, authToken: any, categoryId: number) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/categories/${categoryId}`, 'DELETE', {}, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res
  }

  deleteProtocol = async (baseUrl: any, authToken: any, protocolId: number) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/protocols/${protocolId}`, 'DELETE', {}, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }

    return { result: true }
  }

  archiveProtocol = async (baseUrl: any, authToken: any, protocolId: number) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/protocols/archive/${protocolId}/archived?isArchived=true`, 'PATCH', true, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res
  }

  restoreProtocol = async (baseUrl: any, authToken: any, protocolId: number) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/protocols/archive/${protocolId}/archived?isArchived=false`, 'PATCH', true, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res
  }

  // Extensions a browser renders in a tab, and the type to hand it so that it
  // does. Everything absent from here is downloaded instead.
  VIEWABLE_FILE_TYPES: Record<string, string> = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    txt: 'text/plain;charset=utf-8',
  }

  downloadProtocolFile = async (baseUrl: any, authToken: any, protocolId: number, fileId: number, fileName: string) => {
    try {
      const response = await this.secure.get(`${baseUrl}api/protocols/${protocolId}/files/${fileId}/download`, {
        headers: { 'Authorization': `Bearer ${authToken}` },
        responseType: 'blob',
        withCredentials: true,
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      snackBar.showError(i18next.t('app:file_download_error'))
    }
  }

  // Opens a protocol file in a new tab for reading. The download endpoint needs
  // an Authorization header, so we can't point a plain link at it — fetch the
  // blob and open it via an object URL instead. The blank tab is opened
  // synchronously (before the await) so it survives popup blockers.
  openProtocolFile = async (baseUrl: any, authToken: any, protocolId: number, fileId: number, fileName: string) => {
    const newTab = window.open('', '_blank')
    try {
      const response = await this.secure.get(`${baseUrl}api/protocols/${protocolId}/files/${fileId}/download`, {
        headers: { 'Authorization': `Bearer ${authToken}` },
        responseType: 'blob',
        withCredentials: true,
      })
      // The download endpoint serves everything as a generic attachment type,
      // which every browser saves rather than renders. For the types a browser
      // can actually display, name the type ourselves so the tab shows the file;
      // anything else (a .pptx, say) keeps the server's type and is saved, which
      // is the only thing a browser can do with it.
      const extension = fileName.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1]
      const viewableType = extension ? this.VIEWABLE_FILE_TYPES[extension] : undefined
      const blob = viewableType
        ? new Blob([response.data], { type: viewableType })
        : response.data
      const url = window.URL.createObjectURL(blob)
      if (newTab) {
        newTab.location.href = url
      } else {
        window.open(url, '_blank')
      }
      // Revoke after the tab has had time to load the resource.
      setTimeout(() => window.URL.revokeObjectURL(url), 60000)
    } catch (e) {
      if (newTab) newTab.close()
      snackBar.showError(i18next.t('app:file_open_error'))
    }
  }

  createProtocol = async (baseUrl: any, authToken: any, body: any) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/protocols`, 'POST', body, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      const serverMessage: string = res.data?.message || ''
      const detail = serverMessage.includes('Internal server error.')
        ? serverMessage.split('Internal server error.').pop()?.trim() || ''
        : ''
      const hebrewErrors: Record<string, string> = {
        'ProtocolDate cannot be in the future': i18next.t('app:protocol_date_future'),
      }
      const hebrewMsg = hebrewErrors[detail]
      snackBar.showError(hebrewMsg || res.errorDescription)
      return null
    }
    return res
  }

  getProtocolForView = async (baseUrl: any, authToken: any, protocolId: number) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/protocols/${protocolId}/for-view`, 'GET', {}, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res
  }

  updateProtocol = async (baseUrl: any, authToken: any, protocolId: number, body: object) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/protocols/${protocolId}`, 'PUT', body, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res
  }

  publishProtocol = async (baseUrl: any, authToken: any, protocolId: number) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/protocols/${protocolId}/publish`, 'PUT', {}, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res
  }

  updateProtocolStatus = async (baseUrl: any, authToken: any, protocolId: number, body: { protocolStatusId: number; rejectReason: string }) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/protocols/${protocolId}/status`, 'PUT', body, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res
  }

  getProtocolForEdit = async (baseUrl: any, authToken: any, protocolId: number) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/protocols/${protocolId}/for-edit`, 'GET', {}, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res
  }

  getProtocolForEditStatus = async (baseUrl: any, authToken: any, protocolId: number) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/protocols/${protocolId}/for-edit-status`, 'GET', {}, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res
  }

  updateProtocolFiles = async (baseUrl: any, authToken: any, protocolId: number, fileIdsToDelete: number[], filesToUpload: { file: File; isMainFile: boolean }[] = []) => {
    const formData = new FormData()
    fileIdsToDelete.forEach(id => formData.append('FileIdsToDelete', String(id)))
    filesToUpload.forEach(({ file, isMainFile }, i) => {
      formData.append(`FilesToUpload[${i}].file`, file)
      formData.append(`FilesToUpload[${i}].isMainFile`, String(isMainFile))
    })
    const res = await this.newProtocolFetcher(`${baseUrl}api/protocols/${protocolId}/files`, 'PUT', formData, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res
  }

  uploadProtocolFiles = async (baseUrl: any, authToken: any, protocolId: number, files: { file: File; isMainFile: boolean }[]) => {
    const formData = new FormData()
    files.forEach(({ file, isMainFile }, i) => {
      formData.append(`[${i}].file`, file)
      formData.append(`[${i}].isMainFile`, String(isMainFile))
    })
    const res = await this.newProtocolFetcher(`${baseUrl}api/protocols/${protocolId}/files`, 'POST', formData, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res
  }

  getProtocolUserList = async (baseUrl: any, authToken: any) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/user/list`, 'GET', {}, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }
    return res || []
  }

  getProtocolUserNames = async (baseUrl: any, authToken: any) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/user/names`, 'GET', {}, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }
    return res || []
  }

  getMainManagers = async (baseUrl: any, authToken: any) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/user/main-managers`, 'GET', {}, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }
    return res || []
  }

  getProtocolUsers = async (baseUrl: any, authToken: any) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/user`, 'GET', {}, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }
    return res || []
  }

  getProtocolUserPermissions = async (baseUrl: any, authToken: any) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/user/permissions`, 'GET', {}, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res || null
  }

  getProtocolTermsOfUse = async (baseUrl: any, authToken: any) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/user/terms-of-use`, 'GET', {}, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res || null
  }

  acceptProtocolTermsOfUse = async (baseUrl: any, authToken: any) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/user/terms-of-use`, 'PATCH', { isAcceptedTerms: true }, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res
  }

  patchMainManagerWithEmail = async (baseUrl: any, authToken: any, body: { isMainManager: boolean; userId: number; protocolEmail: string }[]) => {
    const res = await this.newProtocolFetcher(`${baseUrl}api/user/role/main-manager-with-email`, 'PATCH', body, {'Authorization': `Bearer ${authToken}`})
    if (res?.status === 401) return { unauthorized: true }
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return null
    }
    return res
  }

  getUserManagementList = async (communityId: any) => {
    const res = await this.newFetcher(endpoint.getUserManagementList(communityId), 'GET', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return {
        totalCount: 0,
        items: []
      }
    }
    return res || { totalCount: 0, items: [] }
  }

  uploadIsraelId = async (data: any, brandId: number) => {
    const res = await this.newFetcher(endpoint.uploadIsraelId(brandId), 'PUT', data)
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }
    return res || []
  }
  createLabelAssociatedEntity = async (entityId: any, data: any) => {
    try {
      const res = await this.newFetcher(endpoint.createLabelAssociatedEntity(entityId), 'PUT', data)
    
      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return []
      } 

      snackBar.showSuccess(i18next.t('app:updated_ok'))
      return res || []
    } catch (error) {
      snackBar.showError(this.genericErrorText)
      return []
    }
  }

  updateCommunity = async (id: number, data: object) => {
    const res = await this.newFetcher(endpoint.updateCommunity(id), 'PUT', data)
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }
    return res
  }

  sendAlarmMessage = async(data: AlarmMessage) => {
    const res = await this.newFetcher(endpoint.newAlarmMessage(), 'POST', data)

    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }

    if (res.status == 'error') {
      snackBar.showError(res.errorDescription)
      return []
    }

    return res || []
  }

  updateDeviceLocation = async (messageId: number, data: object) => {
    const res = await this.newFetcher(endpoint.updateLocation(messageId), 'PUT', data)

    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }

    if (res.status == 'error') {
      snackBar.showError(res.errorDescription)
      return []
    }

    return res || []
  }

  sendDeviceLocation = async(data: object) => {
    const res = await this.newFetcher(endpoint.getDeviceLocation(), 'PUT', data)

    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }

    if (res.status == 'error') {
      snackBar.showError(res.errorDescription)
      return []
    }

    return res || []
  }

  updateCommunities = async (communityId: ID, data: CommunityUpdateReq) => {
    const res = await this.newFetcher(endpoint.updateCommunities(communityId), 'PUT', data)
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }
    return res || []
  }

  getCommunity = async (communityId: number) => {
    const res = await this.newFetcher(endpoint.manageCommunity(communityId), 'GET', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }
    return res || []
  }

  getCommunitySettings = async (communityId: number) => {
    const res = await this.newFetcher(endpoint.getCommunitySettings(communityId), 'GET', {})
    if (res?.status && res.status == 403) {
    return []
    } else if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }
    return res || []
  }

  updateCommunitySettings = async (communityId: number, data: any) => {
    const res = await this.newFetcher(endpoint.getCommunitySettings(communityId), 'PUT', data)
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }

    snackBar.showSuccess(i18next.t('app:updated_ok'))
    return res || []
  }

  getBrands = async () => {
    const res = await this.newFetcher(endpoint.getBrands(), 'GET', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }
    return res || []
  }

  getCommunitiesUsers = async (communityId: ID): Promise<User[]> => {
    const res = await this.newFetcher(endpoint.getCommunitiesUsers(communityId), 'GET', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }
    return res.map((rawUser: any) => {
      let phones = []
      let emails = []
      try {
        if (rawUser.phones) {
          phones = JSON.parse(rawUser.phones)
        }
        if (rawUser.emails) {
          emails = JSON.parse(rawUser.emails)
        }
      } catch (e) {
        console.log('parse user issue ', e)
      }
      rawUser.is_operator = rawUser.is_operator || false
      rawUser.is_director = rawUser.is_director || false
      rawUser.is_showInAlphon = rawUser.is_showInAlphon || false
      rawUser.is_community_enabled = rawUser.is_community_enabled || false
      return { ...rawUser, phones: phones, emails: emails, app: rawUser.app ? rawUser.app : 'none' }
    })
  }

  getGroups = async (communityId: number, currentMemberId: number | string = '') => {
    const res = await this.newFetcher(endpoint.getGroups(communityId, currentMemberId), 'GET', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }
    if (res.length) {
      return res.map((g: any) => {
        return ({
          group: {
            community_id: g.community_id,
            id: g.id,
            description: /^{"/.test(g.description) ? JSON.parse(g.description) : g.description,
            name: g.name,
            icon: g.icon,
            members: g.members,
            orgainzers: g.orgainzers,
            group_is_member: g.group_is_member != null ? g.group_is_member : false,
            group_is_subscriber: g.group_is_subscriber != null ? g.group_is_subscriber : false,
            group_is_favourite: g.group_is_favourite != null ? g.group_is_favourite : false,
            group_is_sender: g.group_is_sender != null ? g.group_is_sender : false,
            duplex: g.duplex,
            type: GroupType.REGULAR,
            mcEntity: g.mcEntity,
            mcEntityID: g.mcEntityID,
            providerId: g.providerId || null,
            providerName: g.providerName || null,
            sendSMS: g.sendSMS || false,
            sendEmail: g.sendEmail || false,
            allowShare: g.allowShare || false,
            groupLabels: g.groupLabels
          },
          is_favourite: g.is_favourite,
          is_organizer: g.is_organizer,
          is_sender: g.is_sender,
          is_subscriber: g.is_subscriber,
          is_member: g.is_member,

          user: g.user_id
        })
      })
    }
    return []
  }


  getGroupUsers = async (groupId: ID) /*: Promise<{ user_id: number, user_full_name: string, family_name: string, given_name: string, nick_name: string, is_favourite: boolean, is_member: boolean, is_organizer: boolean, is_sender: boolean, is_subscriber: boolean }[]> */ => {
    const res = await this.newFetcher(endpoint.getGroupUsers(groupId), 'GET', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }
    return res || []
  }

  getGroupMessages = async (groupId: number): Promise<UserMessage[]> => {
    const res = await this.newFetcher(endpoint.getGroupMessages(groupId), 'GET', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }
    //generate userGroups from response
    const userGroups: UserMessage[] = res?.map((rawMessage: any) => {
      let msg: UserMessage = {
        user: userStore.user?.user_id,
        status: rawMessage.status,
        response: rawMessage.response ? rawMessage.response : 0,
        message: {
          author_name: rawMessage.author_name,
          create_guid: rawMessage.create_guid,
          created: rawMessage.created ? new Date(rawMessage.created) : rawMessage.created,
          expiry: rawMessage.expiry ? new Date(rawMessage.expiry) : rawMessage.expiry,
          group: rawMessage.group_ref,
          id: rawMessage.id,
          msg_strategy: rawMessage.msg_strategy,
          payload: rawMessage.payload,
          received: rawMessage.received,
          schedule: rawMessage.schedule ? new Date(rawMessage.schedule) : rawMessage.schedule,
          senderId: rawMessage.author_ref,
          senderName: rawMessage.author_name,
          updated: rawMessage.updated ? new Date(rawMessage.updated) : rawMessage.updated,
          locationRequest: rawMessage.locationRequest ? rawMessage.locationRequest : false 
        }
      }
      return msg
    }) || []
    return userGroups
  }

  lazyloadGroupMessages = async ({ pageNumber, pageSize, groupId }: { pageNumber: number, pageSize: number, groupId: number }) => {
    try {
      const res = await this.newFetcher(endpoint.getGroupMessages(groupId, pageNumber, pageSize), 'GET', {})
      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription || this.genericErrorText)
        return []
      }
      return res
    } catch (error) {
      snackBar.showError(this.genericErrorText)
    }
    return []
  }

  getCommunityMessages = async (communityId: number): Promise<UserMessage[]> => {
    const res = await this.newFetcher(endpoint.getCommunityMessages(communityId), 'GET', {});
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }
    return res?.map((rawMessage: any) => {
      let msg: UserMessage = {
        user: userStore.user?.user_id,
        status: rawMessage.status,
        response: rawMessage.response ? rawMessage.response : 0,
        message: {
          author_name: rawMessage.authorName,
          create_guid: '',
          created: rawMessage.created ? new Date(rawMessage.created) : rawMessage.created,
          expiry: rawMessage.expiry ? new Date(rawMessage.expiry) : rawMessage.expiry,
          group: rawMessage.groupId,
          id: rawMessage.id,
          msg_strategy: rawMessage.msgStrategy,
          payload: rawMessage.payload,
          received: rawMessage.received,
          schedule: rawMessage.schedule ? new Date(rawMessage.schedule) : rawMessage.schedule,
          senderId: 0,
          senderName: rawMessage.authorName,
          updated: rawMessage.updated ? new Date(rawMessage.updated) : rawMessage.updated,
          locationRequest: false,
          unreadCounter: rawMessage.unreadCounter
        }
      }
      return msg
    }) || []
  }

  deleteMessage = async (messageId: number) => {
    return await this.newFetcher(endpoint.deleteMessage(messageId), 'DELETE', {});
  }

  sendScheduled = async (messageId: number) => {
    return await this.newFetcher(endpoint.sendScheduled(messageId), 'PUT', {});
  }

  OpenModule = async (moduleId: number, communityId: number, infoType: ModuleEnum, providerEnum?: ProviderEnum, callback?: (any: any) => void, tryWhenInit?: boolean) => {
    try {
      const body = {
        communityId
      }
      const res = await this.newFetcher(endpoint.openModule(moduleId), 'GET', body);
      if (res?.status && res.status >= 400) {
        if (tryWhenInit) {
          return []
        }
        snackBar.showError(res.errors?.[0]?.errorDescription || res.errorDescription)
        return []
      }
      if (res.errors?.length === 0 && res.data != '') {
        switch (infoType) {
          case ModuleEnum.SLIKA:
            return res;
          case ModuleEnum.PROTOCOLS:
            return res;  
          case ModuleEnum.PORTAL:
            switch (providerEnum) {
              case ProviderEnum.MAOF:
                if (res.data) {
                  const cookieData = JSON.parse(res.data)
                  if ('ConcatToUrl' in cookieData && callback) {
                    callback(cookieData['ConcatToUrl'])
                  }
                }
                break;
              case ProviderEnum.MEKOME:
                if (callback) {
                  callback(res.data)
                }
                break;
              default:
                break;
            }
            break;
          default:
            break;
        }
      }
      return res;
    } catch (e) {
      console.log("response2")   
      console.log('error:', e)
    }
  }
  getBudget = async (moduleId: number, communityId: number, infoType: ModuleEnum, year?: number, month?: number) => {
    let res = null;
    if (year || month) {
      if (typeof orgs.currentOrgId !== 'number') {
        return;
      }
      const body = {
        year: year,
        month: month,
        ignoreOTPSave: true,
      }
      res = await this.newFetcher(endpoint.getPayment(orgs.currentOrgId), 'GET', body);
    } else {
      res = await this.OpenModule(moduleId, communityId, infoType, undefined, undefined, true)
    }
    if ((res?.status && res.status >= 400) || res?.errors?.length) {
      throw res;
    }
    try {
      const budget = JSON.parse(res.budget)
      const extraData = (res.extraData && JSON.parse(res.extraData)) || null
      if (budget?.data?.[0]) {
        budget.data[0].extraData = extraData
      }
      return budget?.data?.[0]
    } catch (e) {
      throw e;
    }
  }

  getFiles = async (year: number, month: number) => {
    if (typeof orgs.currentOrgId !== 'number') {
      return;
    }
    const body: any = {
        year: year,
        month: month,
        ignoreOTPSave: true,
        files: true
    }
    const res = await this.newFetcher(endpoint.getPayment(orgs.currentOrgId), 'GET', body);
    if ((res?.status && res.status >= 400) || res?.errors?.length) {
      throw new Error('Failed to load.')
    }
    return ({ fileDates: res.fileDates, files: res.files })
  }

  getFile = async (fileId: string) => {
    if (!orgs.currentOrgId) {
      return;
    }
    const res = await this.newFetcher(endpoint.downloadPayment(orgs.currentOrgId, true), 'GET', { fileId });
    if ((res?.status && res.status >= 400) || res?.errors?.length) {
      throw res
    }
    return res
  };

  getPos = async (year: number, month: number) => {
    if (!orgs.currentOrgId) {
      return;
    }
    const body: any = {
      files: false,
      year: year,
      month: month,
      isPos: true
    }
    const res = await this.newFetcher(endpoint.getPayment(orgs.currentOrgId), 'GET', body);
    if ((res?.status && res.status >= 400) || res?.errors?.length) {
      snackBar.showError(res.errorDescription)
      return { data: [] }
    }
    return JSON.parse(res.data);
  }

  createAssociatedGroups = async (data: any) => {
    try {
      const response = await this.newFetcher(endpoint.createAssociatedGroups(), "POST", {
        id: 0,
        name: data.name,
        smsCounter: parseInt(data.smsCounter),
        extraData: JSON.stringify(data.extraData),
        status: data.status
      })

      if (response?.status && response.status >= 400) {
        throw new Error(response.errorDescription)
      }

      return true

    } catch (error) {
      console.log('update community modules failed', error)
      snackBar.showError(this.genericErrorText)
      return false
    }
  }

  updateAssociatedGroups = async (entityId: number, data: any) => {
    try {
      const response = await this.newFetcher(endpoint.updateAssociatedGroups(entityId), "PUT", {
        id: entityId,
        name: data.name,
        smsCounter: parseInt(data.smsCounter),
        extraData: data.extraData,
        status: data.status
      })

      if (response?.status && response.status >= 400) {
        throw new Error(response.errorDescription)
      }

      return true
    } catch (error) {
      console.log('update community modules failed', error)
      snackBar.showError(this.genericErrorText)
      return false
    }
  }

  getAssociatedGroups = async () => {
    return await this.newFetcher(endpoint.getAssociatedGroups(), 'GET', {});
  }

  getAssociatedLabels = async (council = 1) => {
    return await this.newFetcher(endpoint.getAssociatedLabels(council), 'GET', {});
  }

  getAllLabels = async () => {
    return await this.newFetcher(endpoint.getAllLabels(), 'GET', {});
  }


  getAssociatedLabelsV2 = async (entityId: any) => {
    return await this.newFetcher(endpoint.getAssociatedLabelsV2(entityId), 'GET', {});
  }

  getAssociatedUsers = async (entityId: any) => {
    return await this.newFetcher(endpoint.getAssociatedUsers(entityId), 'GET', {});
  }

  getAssociatedLabelByEntity = async (entityId: any) => {
    return await this.newFetcher(endpoint.getAssociatedLabels(1), 'GET', {});
  }

  setAssociatedGroups = async (mcEntityId: number, groupId: number, labels: Array<string>) => {
    const res = await this.newFetcher(endpoint.updateGroupLabels(groupId, mcEntityId), 'PUT', labels);
    if ((res?.status && res.status >= 400) || res?.errors?.length) {
      snackBar.showError(res.errorDescription || this.genericErrorText)
    }
    return res
  }

  getCommunityDirectorModules  = async (communityId: number) => {
    try {
      const response = await this.newFetcher(endpoint.getCommunityDirectorModules(communityId), "GET", {})

      if (response?.status && response.status >= 400) {
        throw new Error(response.errorDescription)
      }
      return response

    } catch (error) {
      console.log('get community modules failed', error)
      snackBar.showError(this.genericErrorText)
    }
    return []
  }

  getCommunityModules = async (communityId: number) => {
    try {
      const response = await this.newFetcher(endpoint.getCommunityModules(communityId), "GET", {})

      if (response?.status && response.status >= 400) {
        throw new Error(response.errorDescription)
      }
      return response

    } catch (error) {
      console.log('get community modules failed', error)
      snackBar.showError(this.genericErrorText)
    }
    return []
  }

  deleteCommunityModules = async (id: number) => {
    try {
      const response = await this.newFetcher(endpoint.manageCommunityModules(id), "DELETE", {})
      if (response?.status && response.status >= 400) {
        throw new Error(response.errorDescription)
      }
    } catch (error) {
      console.log('DELETE community modules failed', error)
      snackBar.showError(this.genericErrorText)
    }
  }
  addCommunityModules = async (module: ModuleType) => {
    try {
      const response = await this.newFetcher(endpoint.manageCommunityModules(), "POST", {
        module_id: module.module_id,
        community_ref: module.community_ref,
        type: module.type,
        directorPermission: module.directorPermission,
        enable: module.enable,
        external_url: module.external_url,
        title: module.title,
        description: module.description,
        icon_color: module.icon_color,
        icon_text: module.icon_text,
        icon_name: module.icon_name,
        icon_text_settings: module.icon_text_settings,
        org_id: module.org_id,
        provider: module.provider,
        information: module.information,
        data: module.data
      })

      if (response?.status && response.status >= 400) {
        throw new Error(response.errorDescription)
      }
      return response;
    } catch (error) {
      console.log('update community modules failed', error)
      snackBar.showError(this.genericErrorText)
    }
    return {}
  }

  updateCommunityModuleByDirector = async (module: ModuleType) => {
    try {
      const response = await this.newFetcher(endpoint.manageCommunityModulesByDirector(module.module_id, module.enable), "PATCH", {})

      if (response?.status && response.status >= 400) {
        throw new Error(response.errorDescription)
      }

    } catch (error) {
      console.log('update community modules failed', error)
      snackBar.showError(this.genericErrorText)
    }
  }

  updateCommunityModules = async (module: ModuleType) => {
    try {
      const response = await this.newFetcher(endpoint.manageCommunityModules(module.module_id), "PUT", {
        module_id: module.module_id,
        community_ref: module.community_ref,
        type: module.type,
        directorPermission: module.directorPermission,
        enable: module.enable,
        external_url: module.external_url,
        title: module.title,
        description: module.description,
        icon_color: module.icon_color,
        icon_text: module.icon_text,
        icon_name: module.icon_name,
        icon_text_settings: module.icon_text_settings,
        org_id: module.org_id,
        provider: module.provider,
        share: module.share,
        shareEmail: module.shareEmail,
        information: module.information,
        data: module.data
      })

      if (response?.status && response.status >= 400) {
        throw new Error(response.errorDescription)
      }

    } catch (error) {
      console.log('update community modules failed', error)
      snackBar.showError(this.genericErrorText)
    }
  }


  sendMessage = async (requestOps: MessageOp[], blobFile: File | File[] | null = null, groupType: GroupType) => {
    try {
      const user = userStore.user

      const formData = new FormData()
      formData.append('json', JSON.stringify({
        protocol: PROTOCOL_VERSION,
        type: "application/json",
        myId: user?.user_id,
        // myToken: user?.user_token,
        version: user?.version ? user.version : 0,
        requestOps: requestOps
      }))

      if (Array.isArray(blobFile)) {
        for (let x = 0; x < blobFile.length; x++) {
          formData.append("file[]", blobFile[x], x == 1 ? "video.mp4" : "image.png");
        }
      }
      else if (blobFile) {
        formData.append("file", blobFile, "image.png")
      }

      const requestOptions = requestOps[0];
      const res = await this.newFetcher(endpoint.sendMessage(requestOptions.group, !!requestOptions.schedule, requestOptions.op === OperationType.CREATE_SUPPORT_MESSAGE), 'POST', formData)
      let data;
      if (typeof res === 'string') {
        try {
          data = JSON.parse(res)
        } catch (e) {
          data = { results: [{ status: 'fail', errorDescription: 'Fail' }] }
        }
      } else {
        data = res;
      }
      const error = !data || data?.results?.find((el: any) => el.status?.toLowerCase?.() != 'ok')
      const isError = !data || data?.status && data.status >= 400 || error;
      if (isError) {
        snackBar.showError(data?.errorDescription || this.genericErrorText)
      }
      return { success: isError ? false : true, data: data }

    }
    catch (e) {
      console.log(e)
    }
  }

  deleteUserPushToken = async (userId: number) => {
    await this.newFetcher(endpoint.deleteUserPushTokens(userId), 'DELETE', {})
  }

  messageDetails = async (messageId: number) => {
    const res = await this.newFetcher(endpoint.messageDetails(messageId), 'GET', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }
    return res || []
  }

  responseMessage = async (msg: MessageResponseOp | MessageResponseOp[]) => {
    if (Array.isArray(msg)) {
      return await this.newFetcher(endpoint.responseMessages(), "PUT", msg)
    }
    return await this.newFetcher(endpoint.responseMessage(msg.messageId as number), "PUT", msg)
  }

  responseGroupMessages = async (groupId: number, status: number) => {
    return await this.newFetcher(endpoint.responseGroupMessages(groupId), "PUT", {
      response: 0,
      status
    })
  }
  
  getMessageResponses = async (messageId: number) => {
    const res = await this.newFetcher(endpoint.getMessageResponses(messageId), 'GET', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription)
      return []
    }
    return res || []
  }

  getOpenSupportCalls = async (communityId: number) => {
    try {
      const res = await this.newFetcher(endpoint.getOpenSupportCalls(communityId), 'GET', {})
      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return [];
      }
      return mapGroupsDescription(res, GroupType.SUPPORT)
    }
    catch (error) {
      console.log('getOpenSupportCalls', error)
    }
    return []
  }

  getLastUserLocation = async (communityId: number, queryParams: any) => {
    try {
      const queryStr = new URLSearchParams(queryParams).toString()
      const res = await this.newFetcher(`${endpoint.getLastUserLocation(communityId)}?${queryStr}`, 'GET', {})
      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return [];
      }
      return res
    }
    catch (error) {
      console.log('getOpenSupportCalls', error)
    }
    return []
  }

  getLocationLog = async (communityId: number, queryParams: any) => {
    try {
      const queryStr = new URLSearchParams(queryParams).toString()
      const res = await this.newFetcher(`${endpoint.getLocationLog(communityId)}?${queryStr}`, 'GET', {})
      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return [];
      }
      return res
    }
    catch (error) {
      console.log('getOpenSupportCalls', error)
    }
    return []
  }

  getSupportGroups = async (communityId: number, isDirector: boolean): Promise<UserGroup[] | undefined> => {
    try {
      const res = await this.newFetcher(endpoint.getSupportGroups(communityId), 'GET', {})
      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return;
      }

      return mapGroupsDescription(res, GroupType.SUPPORT, isDirector)
    }
    catch (error) {
      console.log('getSupportGroups error: ', error)
    }
    return []
  }

  updateMessageWithMedia = async (messageId: number, requestOps: MessageOp[], blobFile: File | File[] | null = null) => {
    const formData = new FormData()
    const user = userStore.user

    const json = {
      protocol: PROTOCOL_VERSION,
      type: "application/json",
      version: user?.version ? user.version : 0,
      ...requestOps[0]
    }

    formData.append('json', JSON.stringify(json))

    if (Array.isArray(blobFile)) {
      for (let x = 0; x < blobFile.length; x++) {
        formData.append("file[]", blobFile[x], x == 1 ? "video.mp4" : "image.png");
      }
    }
    else if (blobFile) {
      formData.append("file", blobFile, "image.png")
    }

    const res = await this.newFetcher(endpoint.updateMessageWithMedia(messageId), 'PUT', formData) 
    
    if (res.errors?.length) {
      snackBar.showError(res.errors[0]?.errorDescription)
    }

    return res
  }

  updateMessage = async (msg: MessageOp) => {
    return await this.newFetcher(endpoint.updateMessage(msg.message as number), "PUT", msg)
  }

  createCommunity = async (name: string) => {
    const data: CreateCommunityReq = {
      id: 1,
      name,
      guid: newGuid(),
    }
    return await this.newFetcher(endpoint.createCommunity(), "POST", data)
  }

  createGroup = async (userGroup: UserGroup) => {
    const data: UserGroupCreateReq = {
      community: userGroup.group.community_id,
      guid: newGuid(),
      name: userGroup.group.name,
      description: userGroup.group.description === '' ? '' : JSON.stringify(userGroup.group.description),
      icon: userGroup.group.icon || null,
      organizer: !!userGroup.is_organizer,
      sender: !!userGroup.group.group_is_sender,
      subscriber: !!userGroup.group.group_is_subscriber,
      member: !!userGroup.group.group_is_member,
      favourite: !!userGroup.group.group_is_favourite,
      hide: !!userGroup.is_hide,
      sms: !!userGroup.group.sendSMS,
      duplex: userGroup.group.duplex,
      id: 1,
      SendSMS: !!userGroup.group.sendSMS,
      SendEmail: !!userGroup.group.sendEmail,
      allowShare: !!userGroup.group.allowShare,
    }
    return await this.newFetcher(endpoint.createGroup(), "POST", data)
  }

  updateGroup = async (userGroup: UserGroup) => {
    const data = {
      community: userGroup.group.community_id,
      name: userGroup.group.name,
      description: (userGroup.group.description === '' || (userGroup.group.description as string)?.match?.(/^((\\*"*)*)$/g)) ? '' : JSON.stringify(userGroup.group.description),
      icon: userGroup.group.icon || null,
      sender: !!userGroup.group.group_is_sender,
      subscriber: !!userGroup.group.group_is_subscriber,
      member: !!userGroup.group.group_is_member,
      favourite: !!userGroup.group.group_is_favourite,
      hide: !!userGroup.is_hide,
      sms: !!userGroup.group.sendSMS,
      duplex: userGroup.group.duplex,
      SendSMS: !!userGroup.group.sendSMS,
      SendEmail: !!userGroup.group.sendEmail,
      allowShare: !!userGroup.group.allowShare,
    }
    return await this.newFetcher(endpoint.updateGroup(userGroup.group.id), "PUT", data)
  }

  removeGroup = async (data: any) => {
    return await this.newFetcher(endpoint.deleteGroupCommunity(data.group, data.community), "DELETE", {})
  }

  createUser = async (user: User, communityId: number) => {
    const data = {
      givenName: user.given_name,
      familyName: user.family_name,
      nickName: user.nick_name,
      guid: newGuid(),
      israeliID: /\*+\d+/ig.test(user.israeliID) ? null : user.israeliID,
      phones: user.phones,
      emails: user.emails,
      communityId: communityId,
      sendSMS: user.sendSMS,
      sendEmail: user.sendEmail
    }
    return await this.newFetcher(endpoint.createUser(), "POST", data)
  }

  updateUser = async (communityId: number, user: User, userId?: number) => {
    const convertedUser: any = {
      givenName: user.given_name,
      familyName: user.family_name,
      nickName: user.nick_name,
      phones: user.phones,
      israeliID: /\*+\d+/ig.test(user.israeliID) ? null : user.israeliID,
      emails: user.emails,
      communityId: communityId,
      sendSMS: user.sendSMS,
      sendEmail: user.sendEmail
    }
    return await this.newFetcher(endpoint.updateUser(userId ? userId : user.user_id), 'PUT', convertedUser)
  }

  createUsers = async (users: CsvUser[], communityId: number): Promise<{ createdUsers: number[], errorMessages: string[][] }> => {
    const data: any[] = []
    users.forEach((csvUser: CsvUser) => {
      data.push({
        israeliID: csvUser.user.israeliID,
        givenName: csvUser.user.given_name,
        familyName: csvUser.user.family_name,
        nickName: csvUser.user.nick_name,
        guid: newGuid(),
        phones: csvUser.user.phones,
        emails: csvUser.user.emails,
        communityId: communityId,
        sendSMS: csvUser.user.sendSMS,
        sendEmail: csvUser.user.sendEmail
      })
    })
    return await this.newFetcher(endpoint.createUsers(), "POST", data)
  }

  deleteUserCommunity = async (data: { community: number; user: number }) => {
    return await this.newFetcher(endpoint.deleteUserCommunity(data.community, data.user), "DELETE", {})
  }

  updateUserGroups = async (userGroups: UserGroup[], updateOrgenizer: boolean = false, isRemove = false) => {
    const isAdmin = (userGroup: UserGroup) => { return Boolean(intToBool(userGroup.is_organizer) || intToBool(orgs.currentOrg?.is_director || 0)) }

    if (!userGroups || userGroups.length === 0) { return }
    const reqOps = userGroups.map((userGroup) => {
      const requestOp: any = {
        op: OperationType.RELATED_USER_GROUP,
        guid: newGuid(),
        member: intToBool(userGroup.is_member),
        sender: intToBool(userGroup.is_sender),
        subscriber: intToBool(userGroup.is_subscriber),
        favourite: intToBool(userGroup.is_favourite),
        groupId: Math.abs(userGroup.group.id),
        userId: userGroup.user
      }
      if (intToBool(userGroup.is_sender)) {
        requestOp.sender = intToBool(userGroup.is_sender)
      }
      if (isAdmin(userGroup) && updateOrgenizer) {
        requestOp.organizer = isRemove ? false : intToBool(userGroup.is_organizer)
      }
      return requestOp
    })
    return await this.newFetcher(endpoint.updateUserGroups(reqOps[0].userId), 'POST', reqOps)
  }

  updateGroupUsers = async (userGroups: UserGroup[], updateOrgenizer: boolean = false, isRemove = false) => {
    const isAdmin = (userGroup: UserGroup) => { return Boolean(intToBool(userGroup.is_organizer) || intToBool(orgs.currentOrg?.is_director || 0)) }

    if (!userGroups || userGroups.length === 0) { return }
    const reqOps = userGroups.map((userGroup, index) => {
      const requestOp: any = {
        op: OperationType.RELATED_USER_GROUP,
        id: index,
        member: intToBool(userGroup.is_member),
        sender: intToBool(userGroup.is_sender),
        subscriber: intToBool(userGroup.is_subscriber),
        favourite: intToBool(userGroup.is_favourite),
        groupId: Math.abs(userGroup.group.id),
        userId: userGroup.user
      }
      if (intToBool(userGroup.is_sender)) {
        requestOp.sender = intToBool(userGroup.is_sender)
      }
      if (isAdmin(userGroup) && updateOrgenizer) {
        requestOp.organizer = isRemove ? false : intToBool(userGroup.is_organizer)
      }
      return requestOp
    })
    return await this.newFetcher(endpoint.updateGroupUsers(reqOps[0].groupId), 'POST', reqOps)
  }

  resetModuleData = async (moduleId: number, communityId: number, userId?: number) => {
    try {
      const res = await this.newFetcher(endpoint.resetModule(moduleId, communityId), 'PUT', {
        // year: 2022,
        // month: 8,
        communityId
      });
      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return []
      }
      return res;
    } catch (error) {
      snackBar.showError(this.genericErrorText)
      console.log('update community modules failed', error)
    }
  }

  // NOTE: currently unused (callers use updateUsersCommunityRelate / createUsers).
  // Hardened defensively: batch with limited concurrency instead of firing a
  // PUT-per-user flood, and use allSettled so one failure doesn't reject the
  // whole batch. Returns settled results so callers can inspect failures.
  async updateUsers(communityId: number, users: User[]) {
    const CONCURRENCY = 10
    const results: PromiseSettledResult<any>[] = []
    for (let i = 0; i < users.length; i += CONCURRENCY) {
      const chunk = users.slice(i, i + CONCURRENCY)
      const settled = await Promise.allSettled(chunk.map(user => this.updateUser(communityId, user, undefined)))
      results.push(...settled)
    }
    return results
  }
  
  updateUserCommunity = async (user: User, communityId: number, isAdmin: boolean) => {
      const data: any = {
        director: isAdmin,
        operator: isAdmin,
      }
      return await this.newFetcher(endpoint.updateUserCommunityRelate(communityId, user.user_id === true ? user.userID : user.user_id), 'POST', data)
  }
  setUserCommunity = async (communityId: number, enable: boolean) => {
    const res = await this.newFetcher(endpoint.setUserCommunity(communityId, null, enable), 'PUT', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription || this.genericErrorText)
      return;
    }
    return res
  }
  updateUsersCommunityRelate = async (usersIds: number[], communityId: number) => {

    const membersRequestData = usersIds.map((user_id) => {
      const requestOp: any = {
        director: false,
        operator: false,
        enabled: true,
        userId: user_id,
        showInAlphon: true,
      }
      return requestOp
    })
    if (membersRequestData != null && membersRequestData.length > 0) {
      return await this.newFetcher(endpoint.updateUsersCommunityRelate(communityId), 'POST', membersRequestData)
    }
  }

  updateUserAlphon = async (communityId: number, showInAlphon: boolean, otherUserId?: number) => {
    const res = await this.newFetcher(endpoint.setUserCommunity(communityId, otherUserId, undefined, showInAlphon), 'PUT', {})
    if (res?.status && res.status >= 400) {
      snackBar.showError(res.errorDescription || this.genericErrorText)
    }
  }

  budgetOtp1 = async (communityId: number, phone: string, id: string, saveData: boolean) => {
    const body = {
      phoneNumber: phone,
      israeliId: id,
      saveData,
      // Id: id,
    }
    const res = await this.newFetcher(endpoint.paymentLogin(communityId), 'POST', body);
    if (res?.status && res.status >= 400) {
      throw res?.errors
    }
    return res
  }
  budgetVerify = async (communityId: number, pinCode: number) => {
    const res = await this.newFetcher(endpoint.paymentVerify(communityId, pinCode), 'GET', {});
    if ((res?.status && res.status >= 400) || res?.errors?.length) {
      throw res?.errors
    }
    return res
  }
  budgetForget = async (communityId: number) => {
    const res = await this.newFetcher(endpoint.forgetPayment(communityId), 'GET', {});
    if ((res?.status && res.status >= 400) || res?.errors?.length) {
      throw res
    }
    return res;
  }
  budgetResend = async (communityId: number) => {
    const res = await this.newFetcher(endpoint.resendPayment(communityId), 'GET', {});
    if ((res?.status && res.status >= 400) || res?.errors?.length) {
      throw res
    }
    return res?.errors
  }
  signEula = async () => {
    return await this.newFetcher(endpoint.signEula(userStore.user.user_id), 'POST', {});
  }
  getProviders = async () => {
    return await this.newFetcher(endpoint.getProviders(), 'GET', {});
  }
  getSelectedProvider = async (orgId: number) => {
    return await this.newFetcher(endpoint.getCommunityProvider(orgId), 'GET', {});
  }
  updateCommunityProvider = async (providerId: number) => {
    const communityId = orgs.currentOrgId;
    if (!communityId) {
      return;
    }
    return await this.newFetcher(endpoint.updateCommunityProvider(providerId, communityId), 'PUT', {});
  }
  setGroupProvider = async (groupId: number, providerId: number) => {
    return await this.newFetcher(endpoint.setGroupProvider(groupId, providerId), 'PUT', {});
  }
  deleteGroupProvider = async (groupId: number) => {
    return await this.newFetcher(endpoint.deleteGroupProvider(groupId), 'DELETE', {});
  }
  deleteCommunityProvider = async (communityId: number, providerId: number) => {
    return await this.newFetcher(endpoint.deleteProvider(providerId, communityId), 'DELETE', {});
  }

  removeEntityUser = async (entityId: number, userId: number) => {
    try {
      const res =  await this.newFetcher(endpoint.deleteEntityUser(entityId, userId), 'DELETE', {});

      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return []
      }

      snackBar.showSuccess(i18next.t('app:updated_ok'))
      return res

    } catch (error) {
      snackBar.showError(this.genericErrorText)
      return []
    }
  }

  createEntityUser = async (entityId: any, data: any) => {
    const [givenName, ...restNames] = data.name.trim().replace(/(\s{2,})+/, ' ').split(' ')
    const reqData = {
      guid: newGuid(),
      emails: data.emails,
      phones: data.phones,
      givenName: givenName ? givenName : '',
      entityId: entityId,
      nickName: '',
      familyName: Array.isArray(restNames) && restNames.length > 1 ? restNames.join(' ') : '',
    }

    try {
      const res = await this.newFetcher(endpoint.createEntityUser(), "POST", reqData)
      if (res?.status && res.status >= 400) {
        if (res?.data && res.data?.errorMessages && Array.isArray(res.data?.errorMessages) && res.data?.errorMessages.length > 0) {
          const error = JSON.parse(res.data?.errorMessages[0]) 
          //snackBar.showError(error.description)
          return { resErr: true, error: error.description }
        } else {
          //snackBar.showError(res.errorDescription)
          return { resErr: true, error: res.errorDescription }
        }
      }

      snackBar.showSuccess(i18next.t('app:updated_ok'))
      return res

    } catch (error) {
      //snackBar.showError(this.genericErrorText)
      return { resErr: true, error: this.genericErrorText }
    }
  }

  unlinkLabelWithEntity  = async (id: any) => {
    try {
      const res = await this.newFetcher(endpoint.unlinkLabelWithEntity(id), "DELETE", {})
      
      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return null
      }

      snackBar.showSuccess(i18next.t('app:updated_ok'))
      return res

    } catch (error) {
      snackBar.showError(this.genericErrorText)
      return []
    }
  } 

  linkLabelWithEntity = async (entityId: any, labelId: any) => {
    try {
      const res = await this.newFetcher(endpoint.linkLabelWithEntity(), "POST", {
        entityId, 
        labelId, 
        status: 1
      })
      
      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return null
      }

      snackBar.showSuccess(i18next.t('app:updated_ok'))
      return res

    } catch (error) {
      snackBar.showError(this.genericErrorText)
      return []
    }
  } 

  createLabel = async (data: any) => {
    try {
      const res = await this.newFetcher(endpoint.createLabel(), "POST", data)

      if (res?.status && res.status == 422) {
        snackBar.showError(res.errorDescription)
        return null
      }
      
      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return null
      }

      snackBar.showSuccess(i18next.t('app:updated_ok'))
      return res

    } catch (error) {
      snackBar.showError(this.genericErrorText)
      return []
    }
  }

  editEntityUser = async (user: any, data: any, entityId: any) => {
    const [givenName, ...restNames] = data.name.trim().replace(/(\s{2,})+/, ' ').split(' ')
    const reqData = {
      guid: newGuid(),
      emails: Array.isArray(data.emails) && data.emails.length == 1 && data.emails[0].trim() == '' ? [] : data.emails,
      phones: Array.isArray(data.phones) && data.phones.length == 1 && data.phones[0].trim() == '' ? [] : data.phones,
      userId: user.userId,
      givenName: givenName ? givenName : '',
      entityId: entityId,
      nickName: '',
      familyName: Array.isArray(restNames) && restNames.length > 1 ? restNames.join(' ') : '',
    }

    try {
      const res = await this.newFetcher(endpoint.editEntityUser(user.userId), "PUT", reqData)
      
      if (res?.status && res.status >= 400) {
        if (res?.data && res.data?.errorMessages && Array.isArray(res.data?.errorMessages) && res.data?.errorMessages.length > 0) {
          const error = JSON.parse(res.data?.errorMessages[0]) 
          //snackBar.showError(error.description)
          return { resErr: true, error: error.description }
        } else {
          //snackBar.showError(res.errorDescription)
          return { resErr: true, error: res.errorDescription }
        }
      }

      snackBar.showSuccess(i18next.t('app:updated_ok'))
      return res

    } catch (error) {
      //snackBar.showError(this.genericErrorText)
      //return null
      return { resErr: true, error: this.genericErrorText }
    }
  }

  editEntityLabel = async (labelId: any, data: any) => {
    try {
      const res = await this.newFetcher(endpoint.editEntityLabel(labelId), "PUT", data) 
      
      if (res?.status && res.status == 422) {
        snackBar.showError(res.errorDescription)
        return null
      }

      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return []
      }

      snackBar.showSuccess(i18next.t('app:updated_ok'))
      return res

    } catch (error) {
      snackBar.showError(this.genericErrorText)
      return []
    }
  }

  deleteOrgSendingProvider = async (id: any) => {
    try {
      const res = await this.newFetcher(endpoint.updateOrgSendingProviders(id), "DELETE", {})
      
      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return null
      }

      snackBar.showSuccess(i18next.t('app:updated_ok'))
      return res

    } catch (error) {
      snackBar.showError(this.genericErrorText)
      return []
    }
  }
  
  deleteGroupSendingProvider = async (id: any) => {
    try {
      const res = await this.newFetcher(endpoint.updateGroupSendingProviders(id), "DELETE", {})
      
      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return null
      }

      snackBar.showSuccess(i18next.t('app:updated_ok'))
      return res

    } catch (error) {
      snackBar.showError(this.genericErrorText)
      return []
    }
  }

  createOrgSendingProvider = async (data: any) => {
    try {
      const res = await this.newFetcher(endpoint.getCommunitySendingProviders(), "POST", data) 
      
      if (res?.status && res.status == 422) {
        snackBar.showError(res.errorDescription)
        return null
      }

      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return []
      }

      snackBar.showSuccess(i18next.t('app:updated_ok'))
      return res

    } catch (error) {
      snackBar.showError(this.genericErrorText)
      return []
    }
  }

  updateOrgSendingProvider = async (id: any, data: any) => {
    try {
      const res = await this.newFetcher(endpoint.updateOrgSendingProviders(id), "PUT", data) 
      
      if (res?.status && res.status == 422) {
        snackBar.showError(res.errorDescription)
        return null
      }

      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return []
      }

      snackBar.showSuccess(i18next.t('app:updated_ok'))
      return res

    } catch (error) {
      snackBar.showError(this.genericErrorText)
      return []
    }
  }

  createGroupSendingProvider = async (data: any) => {
    try {
      const res = await this.newFetcher(endpoint.getGroupSendingProviders(), "POST", data) 
      
      if (res?.status && res.status == 422) {
        snackBar.showError(res.errorDescription)
        return null
      }

      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return []
      }

      snackBar.showSuccess(i18next.t('app:updated_ok'))
      return res

    } catch (error) {
      snackBar.showError(this.genericErrorText)
      return []
    }
  }

  updateGroupSendingProvider = async (id: any, data: any) => {
    try {
      const res = await this.newFetcher(endpoint.updateGroupSendingProviders(id), "PUT", data) 
      
      if (res?.status && res.status == 422) {
        snackBar.showError(res.errorDescription)
        return null
      }

      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return []
      }

      snackBar.showSuccess(i18next.t('app:updated_ok'))
      return res

    } catch (error) {
      snackBar.showError(this.genericErrorText)
      return []
    }
  }

  getGroupSendingProviders = async (communityId: any) => {
    try {
      const res = await this.newFetcher(endpoint.getGroupSendingProviders(), 'GET', {
        communityId
      });
      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return []
      }
      return res;
    } catch (error) {
      snackBar.showError(this.genericErrorText)
    }
    return []
  }

  getCommunitySendingProviders = async (communityId: any) => {
    try {
      const res = await this.newFetcher(endpoint.getCommunitySendingProviders(), 'GET', {
        communityId
      });
      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return []
      }
      return res;
    } catch (error) {
      snackBar.showError(this.genericErrorText)
    }
    return []
  }

  shareInformation = async ( { moduleId, communityId}: { moduleId: number, communityId: number}) => {
    try {
      const res = await this.newFetcher(endpoint.shareModule(moduleId, communityId), 'GET', {
        // year: 2022,
        // month: 8,
        communityId
      });
      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return []
      }
      return res;
    } catch (error) {
      snackBar.showError(this.genericErrorText)
    }
    return []
  }

  getEntityLabelLinks = async (entityId: any) => {
    try {
      const res = await this.newFetcher(endpoint.getEntityLabelLinks(entityId), 'GET', {});
      
      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return []
      }

      return res;
    } catch (error) {
      snackBar.showError(this.genericErrorText)
      return []
    }
    return []
  }

  sendClickAfterExpand = async ( { moduleId, communityId}: { moduleId: number, communityId: number}) => {
    try {
      const res = await this.newFetcher(endpoint.clickModule(moduleId, communityId), 'GET', {
        // year: 2022,
        // month: 8,
        communityId
      });
      if (res?.status && res.status >= 400) {
        snackBar.showError(res.errorDescription)
        return []
      }
      return res;
    } catch (error) {
      snackBar.showError(this.genericErrorText)
    }
    return []
  }
}

  

const API_MEKOMI_BASE_URL = process.env.REACT_APP_API_MEKOMI_BASE_URL
const X_ZUMO_APPLICATION = process.env.REACT_APP_X_ZUMO_APPLICATION

const sApi = axios.create({
  baseURL: `${API_MEKOMI_BASE_URL}`,
  headers: {
    'X-ZUMO-APPLICATION': X_ZUMO_APPLICATION,
    'X-ZUMO-FEATURES': 'AJ'
  }
})

const api = new Api(sApi)
export default api
